/** TanStack Query hooks for document CRUD */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '../lib/api-client'
import type { Document, DocumentVersion } from '@agentwiki/shared'

interface ListDocumentsParams {
  folderId?: string
  /** Project id or slug */
  project?: string
  search?: string
  tag?: string
  category?: string
  page?: number
  limit?: number
  offset?: number
  sort?: string
  order?: string
  enabled?: boolean
}

interface PaginatedDocuments {
  data: Document[]
  total: number
  page: number
  limit: number
}

interface CreateDocumentBody {
  title: string
  description?: string
  project?: string
  folderId?: string | null
  category?: string | null
  accessLevel?: string
  content?: string
  contentJson?: unknown
}

interface UpdateDocumentBody {
  title?: string
  description?: string
  folderId?: string | null
  category?: string | null
  accessLevel?: string
  content?: string
  contentJson?: unknown
  summary?: string | null
  /** The updatedAt the edit is based on; the API answers 409 when the document changed since */
  expectedUpdatedAt?: string | number
}

export function useDocuments(params: ListDocumentsParams = {}) {
  const { enabled, ...queryParams } = params
  const search = new URLSearchParams()
  if (queryParams.folderId) search.set('folderId', queryParams.folderId)
  if (queryParams.project) search.set('project', queryParams.project)
  if (queryParams.search) search.set('search', queryParams.search)
  if (queryParams.tag) search.set('tag', queryParams.tag)
  if (queryParams.category) search.set('category', queryParams.category)
  if (queryParams.page) search.set('page', String(queryParams.page))
  if (queryParams.limit) search.set('limit', String(queryParams.limit))
  if (queryParams.offset) search.set('offset', String(queryParams.offset))
  if (queryParams.sort) search.set('sort', queryParams.sort)
  if (queryParams.order) search.set('order', queryParams.order)

  return useQuery<PaginatedDocuments>({
    queryKey: ['documents', queryParams],
    queryFn: () => apiClient.get<PaginatedDocuments>(`/api/documents?${search}`),
    enabled: enabled ?? true,
  })
}

export function useDocument(id: string | null) {
  return useQuery<Document>({
    queryKey: ['documents', id],
    queryFn: () => apiClient.get<Document>(`/api/documents/${id}`),
    enabled: !!id,
  })
}

export function useDocumentBySlug(slug: string | undefined) {
  return useQuery<Document>({
    queryKey: ['documents', 'by-slug', slug],
    queryFn: () => apiClient.get<Document>(`/api/documents/by-slug/${slug}`),
    enabled: !!slug,
  })
}

/**
 * Document for a /doc/:slug route. Older links put the document id in that slot, so a slug miss
 * falls back to a lookup by id.
 */
export function useDocumentByRef(ref: string | undefined) {
  return useQuery<Document, ApiError>({
    queryKey: ['documents', 'by-ref', ref],
    queryFn: async () => {
      const slug = encodeURIComponent(ref ?? '')
      try {
        return await apiClient.get<Document>(`/api/documents/by-slug/${slug}`)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return apiClient.get<Document>(`/api/documents/${slug}`)
        throw err
      }
    },
    enabled: !!ref,
    retry: (count, err) => err.status !== 404 && count < 1,
  })
}

/** Snapshot content + contentJson into version history (POST /api/documents/:id/versions) */
export function useCreateVersionCheckpoint() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post<{ id: string; version: number }>(`/api/documents/${id}/versions`),
    onSuccess: (_result, id) => qc.invalidateQueries({ queryKey: ['documents', id, 'versions'] }),
  })
}

export function useDocumentVersions(id: string | null) {
  return useQuery<{ versions: DocumentVersion[] }>({
    queryKey: ['documents', id, 'versions'],
    queryFn: () => apiClient.get<{ versions: DocumentVersion[] }>(`/api/documents/${id}/versions`),
    enabled: !!id,
  })
}

export function useCreateDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateDocumentBody) => apiClient.post<Document>('/api/documents', body),
    onSuccess: (doc) => {
      // Cache the newly created document so subsequent useDocument(id) hits cache
      // instead of firing a GET that may 404 due to D1 eventual consistency
      qc.setQueryData(['documents', doc.id], doc)
      qc.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

export function useUpdateDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateDocumentBody & { id: string }) =>
      apiClient.put<{ id: string; projectId: string | null }>(`/api/documents/${id}`, body),
    // The response carries only the id, so refetch instead of caching it as the document
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  })
}

export function useDeleteDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: boolean }>(`/api/documents/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}
